import { useCallback, useMemo, useState } from "react";
import type { TravelWalletOffer } from "@b2bportal/lodging-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, Divider } from "@hopper-b2b/ui";
import clsx from "clsx";
import Chevron from "../../../assets/client/chevron.svg?react";
import Coin from "../../../assets/client/coin.svg";
import { formatExpiresOn } from "../../../utils/formatExpiresOn";
import { FintechBanner } from "../../FintechBanner";
import { FintechHalfSheet } from "../../FintechHalfSheet";
import { HotelOfferTermsModal } from "../../TermsAndConditionsWrapper/component";
import styles from "./styles.module.scss";

interface OfferBannerProps {
  offer: TravelWalletOffer;
  showMore: boolean;
  minimal?: boolean;
  onClick?: () => void;
  generic?: boolean;
}

export const OfferBanner = ({
  showMore,
  offer,
  minimal = false,
  onClick,
  generic = false,
}: OfferBannerProps) => {
  const { t, locale } = useI18nContext();
  const [openHalfSheet, setOpenHalfSheet] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);

  const handleOpenHalfSheet = useCallback(() => {
    onClick && onClick();
    setOpenHalfSheet(true);
  }, [onClick]);

  const handleCloseHalfSheet = useCallback(() => {
    setOpenHalfSheet(false);
  }, []);

  const offerTitle = useMemo(
    () =>
      t(
        generic
          ? "lodgingOfferDiscount.percentOffSelectedHotels"
          : "lodgingOfferDiscount.percentOffReservation",
        {
          discount: getPercentage(offer?.percentage * 100, locale),
        }
      ),
    [offer, t, locale]
  );

  const isWalletOffer = Boolean((offer as any)?.offerContent);
  if (generic && isWalletOffer) {
    return null;
  }

  return (
    <>
      <FintechBanner
        title={offerTitle}
        subtitle={!minimal ? t("hotelVoucherSubtitle") : null}
        fintechIcon={Coin}
        hideBrand
        className={clsx(
          styles.OfferBanner,
          "OfferBanner",
          minimal ? "minimal" : null
        )}
        onClick={showMore ? handleOpenHalfSheet : null}
      />
      {showMore && offer ? (
        <>
          <FintechHalfSheet
            className={clsx(styles.OfferModal, "OfferModal")}
            isOpen={openHalfSheet}
            onClose={handleCloseHalfSheet}
            icon={Coin}
            title={offerTitle}
            description={
              <div>
                <p className={styles.OfferFinePrint}>
                  {t("offersCancellationDisclosure")}
                </p>
                {offer?.expiresOn ? (
                  <p className={styles.OfferFinePrint}>
                    {t("lodgingOfferDiscount.validUntil", {
                      expiryDate: formatExpiresOn(
                        String(offer.expiresOn),
                        locale
                      ),
                      interpolation: { escapeValue: false },
                    })}
                  </p>
                ) : null}
                <Divider />
                <ButtonWrap
                  className={styles.TermButton}
                  onClick={() => setOpenTermsModal(true)}
                >
                  <p className={styles.Terms}>{t("termsAndConditions")}</p>
                  <Chevron />
                </ButtonWrap>
              </div>
            }
          />
          <HotelOfferTermsModal
            openModal={openTermsModal}
            setOpenModal={setOpenTermsModal}
          />
        </>
      ) : null}
    </>
  );
};
